import * as React from 'react';
import { Slot as SlotPrimitive } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
const SIDEBAR_WIDTH_MOBILE = '16rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
interface SidebarContextProps {
  // `undefined` during SSR and the first client render — components
  // that branch on this should defer rendering until it resolves.
  isMobile: boolean | undefined;
  open: boolean;
  openMobile: boolean;
  setOpen: (open: boolean) => void;
  setOpenMobile: (open: boolean) => void;
  state: 'expanded' | 'collapsed';
  toggleSidebar: () => void;
}
const SidebarContext = React.createContext<SidebarContextProps | null>(null);
function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }
  return context;
}
const SidebarProvider = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}>(({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}, ref) => {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback((value: boolean | ((value: boolean) => boolean)) => {
    const openState = typeof value === 'function' ? value(open) : value;
    if (setOpenProp) {
      setOpenProp(openState);
    } else {
      _setOpen(openState);
    }
  }, [setOpenProp, open]);
  const toggleSidebar = React.useCallback(() => isMobile ? setOpenMobile(open => !open) : setOpen(open => !open), [isMobile, setOpen, setOpenMobile]);
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);
  const state = open ? 'expanded' : 'collapsed';
  const contextValue = React.useMemo<SidebarContextProps>(() => ({
    state,
    open,
    setOpen,
    isMobile,
    openMobile,
    setOpenMobile,
    toggleSidebar
  }), [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]);
  return <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div className={cn('group/sidebar-wrapper h-full w-full', className)} ref={ref} style={style} {...props}>
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>;
});
SidebarProvider.displayName = 'SidebarProvider';

/**
 * How far down the page the desktop sidebar should start.
 *
 * The visible sidebar is `position: fixed`, so it ignores anything stacked
 * above it in the flow — a top-sticky banner reserves its space in the
 * layout but the sidebar would still paint over it. The in-flow gap element
 * already sits below those banners, so its viewport offset is exactly what
 * the fixed copy needs, and it falls back to 0 as the banner scrolls away.
 */
const useFixedSidebarTopOffset = (gapRef: React.RefObject<HTMLDivElement | null>) => {
  const [topOffset, setTopOffset] = React.useState(0);
  React.useEffect(() => {
    const gap = gapRef.current;
    if (!gap) {
      return;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      setTopOffset(Math.max(0, Math.round(gap.getBoundingClientRect().top)));
    };
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(measure);
      }
    };
    measure();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);

    // Catches banners mounting, being dismissed, or reflowing to a new height.
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [gapRef]);
  return topOffset;
};
const Sidebar = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}>(({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}, ref) => {
  const {
    state,
    openMobile,
    setOpenMobile
  } = useSidebar();
  const gapRef = React.useRef<HTMLDivElement>(null);
  const topOffset = useFixedSidebarTopOffset(gapRef);
  if (collapsible === 'none') {
    return <div className={cn('flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground', className)} ref={ref} {...props}>
          {children}
        </div>;
  }

  // Render BOTH the mobile Sheet and the desktop tree so the navigation
  // is present in the SSR HTML (crawlers without JS can follow the links).
  // Radix Dialog only mounts <SheetContent>'s children to the DOM when
  // `open === true`, so the desktop tree is the only nav in the document
  // at rest. The desktop tree's `hidden headerFixed:block` keeps it
  // visually scoped to >=600px viewports.
  return <>
        <Sheet onOpenChange={setOpenMobile} open={openMobile}>
          <SheetContent className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden" data-mobile="true" data-sidebar="sidebar" side={side} style={{
        '--sidebar-width': SIDEBAR_WIDTH_MOBILE
      } as React.CSSProperties}>
            <SheetHeader className="sr-only">
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
        <div className="group peer headerFixed:block hidden text-sidebar-foreground" data-collapsible={state === 'collapsed' ? collapsible : ''} data-side={side} data-state={state} data-variant={variant} ref={ref}>
          {/* This is what handles the sidebar gap on desktop */}
          <div ref={gapRef} className={cn('relative w-(--sidebar-width) bg-transparent', 'group-data-[collapsible=offcanvas]:w-0', 'group-data-[side=right]:rotate-180', variant === 'floating' || variant === 'inset' ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]' : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)')} />
          <div className={cn('fixed z-10 headerFixed:flex hidden w-(--sidebar-width)', side === 'left' ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]' : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]', variant === 'floating' || variant === 'inset' ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]' : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)', className)} {...props} style={{
        ...props.style,
        top: topOffset,
        height: `calc(100svh - ${topOffset}px)`
      }}>
            <div className={cn('flex h-full w-full flex-col bg-sidebar', variant === 'floating' ? 'rounded-lg border border-gray-150 border-solid shadow dark:border-gray-800' : side === 'left' ? 'border-0 border-gray-150 border-r border-solid dark:border-gray-800' : 'border-0 border-gray-150 border-l border-solid dark:border-gray-800')} data-sidebar="sidebar">
              {children}
            </div>
          </div>
        </div>
      </>;
});
Sidebar.displayName = 'Sidebar';
const SidebarTrigger = React.forwardRef<React.ElementRef<typeof Button>, React.ComponentProps<typeof Button>>(({
  className,
  onClick,
  ...props
}, ref) => {
  const {
    toggleSidebar
  } = useSidebar();
  return <Button className={cn('h-7 w-7', className)} data-sidebar="trigger" onClick={event => {
    onClick?.(event);
    toggleSidebar();
  }} ref={ref} size="icon" variant="ghost" {...props}>
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>;
});
SidebarTrigger.displayName = 'SidebarTrigger';
const SidebarRail = React.forwardRef<HTMLButtonElement, React.ComponentProps<'button'>>(({
  className,
  ...props
}, ref) => {
  const {
    toggleSidebar
  } = useSidebar();
  return <button aria-label="Toggle Sidebar" className={cn('absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex', '[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize', '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize', 'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:hover:bg-sidebar group-data-[collapsible=offcanvas]:after:left-full', '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2', '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2', className)} data-sidebar="rail" onClick={toggleSidebar} ref={ref} tabIndex={-1} title="Toggle Sidebar" {...props} />;
});
SidebarRail.displayName = 'SidebarRail';
const SidebarInset = React.forwardRef<HTMLDivElement, React.ComponentProps<'main'>>(({
  className,
  ...props
}, ref) => <main className={cn('relative flex w-full flex-1 flex-col bg-white dark:bg-gray-950', 'headerFixed:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 headerFixed:peer-data-[variant=inset]:m-2 headerFixed:peer-data-[variant=inset]:ml-0 headerFixed:peer-data-[variant=inset]:rounded-xl headerFixed:peer-data-[variant=inset]:shadow', className)} ref={ref} {...props} />);
SidebarInset.displayName = 'SidebarInset';
const SidebarInput = React.forwardRef<React.ElementRef<typeof Input>, React.ComponentProps<typeof Input>>(({
  className,
  ...props
}, ref) => <Input className={cn('h-8 w-full bg-white shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring dark:bg-gray-950', className)} data-sidebar="input" ref={ref} {...props} />);
SidebarInput.displayName = 'SidebarInput';
const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({
  className,
  ...props
}, ref) => <div className={cn('flex flex-col gap-2 p-2', className)} data-sidebar="header" ref={ref} {...props} />);
SidebarHeader.displayName = 'SidebarHeader';
const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({
  className,
  ...props
}, ref) => <div className={cn('flex flex-col gap-2 p-2', className)} data-sidebar="footer" ref={ref} {...props} />);
SidebarFooter.displayName = 'SidebarFooter';
const SidebarSeparator = React.forwardRef<React.ElementRef<typeof Separator>, React.ComponentProps<typeof Separator>>(({
  className,
  ...props
}, ref) => <Separator className={cn('mx-2 w-auto bg-sidebar-border', className)} data-sidebar="separator" ref={ref} {...props} />);
SidebarSeparator.displayName = 'SidebarSeparator';
const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({
  className,
  ...props
}, ref) => <div className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden', className)} data-sidebar="content" ref={ref} {...props} />);
SidebarContent.displayName = 'SidebarContent';
const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({
  className,
  ...props
}, ref) => <div className={cn('relative flex w-full min-w-0 flex-col p-2', className)} data-sidebar="group" ref={ref} {...props} />);
SidebarGroup.displayName = 'SidebarGroup';
const SidebarGroupTitle = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & {
  asChild?: boolean;
}>(({
  className,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? SlotPrimitive.Root : 'div';
  return <Comp className={cn('flex h-8 shrink-0 items-center rounded-md px-2 font-medium text-sidebar-foreground/70 text-xs outline-hidden ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0', 'group-data-[collapsible=icon]:hidden', className)} data-sidebar="group-title" ref={ref} {...props} />;
});
SidebarGroupTitle.displayName = 'SidebarGroupTitle';
const SidebarGroupAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<'button'> & {
  asChild?: boolean;
}>(({
  className,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? SlotPrimitive.Root : 'button';
  return <Comp className={cn('absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-hidden ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0', 'after:absolute after:-inset-2 after:headerFixed:hidden', 'group-data-[collapsible=icon]:hidden', className)} data-sidebar="group-action" ref={ref} {...props} />;
});
SidebarGroupAction.displayName = 'SidebarGroupAction';
const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({
  className,
  ...props
}, ref) => <div className={cn('w-full text-sm', className)} data-sidebar="group-content" ref={ref} {...props} />);
SidebarGroupContent.displayName = 'SidebarGroupContent';
const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(({
  className,
  ...props
}, ref) => <ul className={cn('flex w-full min-w-0 list-none flex-col gap-1', className)} data-sidebar="menu" ref={ref} {...props} />);
SidebarMenu.displayName = 'SidebarMenu';
const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({
  className,
  ...props
}, ref) => <li className={cn('group/menu-item relative', className)} data-sidebar="menu-item" ref={ref} {...props} />);
SidebarMenuItem.displayName = 'SidebarMenuItem';
const sidebarMenuButtonVariants = cva('peer/menu-button group-data-[collapsible=icon]:justify-center! flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:h-8! group-data-[collapsible=icon]:w-full! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate group-data-[collapsible=icon]:[&>span:last-child]:hidden [&>svg]:size-4 [&>svg]:shrink-0', {
  variants: {
    variant: {
      default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      outline: 'bg-white shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))] dark:bg-gray-950'
    },
    size: {
      default: 'h-8 text-sm',
      sm: 'h-7 text-xs',
      lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
const SidebarMenuButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>>(({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}, ref) => {
  const Comp = asChild ? SlotPrimitive.Root : 'button';
  const {
    isMobile,
    state
  } = useSidebar();
  const button = <Comp className={cn(sidebarMenuButtonVariants({
    variant,
    size
  }), className)} data-active={isActive} data-sidebar="menu-button" data-size={size} ref={ref} {...props} />;
  if (!tooltip) {
    return button;
  }
  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip
    };
  }
  return <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent align="center" hidden={state !== 'collapsed' || isMobile} side="right" {...tooltip} />
      </Tooltip>;
});
SidebarMenuButton.displayName = 'SidebarMenuButton';
const SidebarMenuAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}>(({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}, ref) => {
  const Comp = asChild ? SlotPrimitive.Root : 'button';
  return <Comp className={cn('absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-hidden ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0', 'after:absolute after:-inset-2 after:headerFixed:hidden', 'peer-data-[size=sm]/menu-button:top-1', 'peer-data-[size=default]/menu-button:top-1.5', 'peer-data-[size=lg]/menu-button:top-2.5', 'group-data-[collapsible=icon]:hidden', showOnHover && 'headerFixed:opacity-0 group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground', className)} data-sidebar="menu-action" ref={ref} {...props} />;
});
SidebarMenuAction.displayName = 'SidebarMenuAction';
const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({
  className,
  ...props
}, ref) => <div className={cn('pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 font-medium text-sidebar-foreground text-xs tabular-nums', 'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground', 'peer-data-[size=sm]/menu-button:top-1', 'peer-data-[size=default]/menu-button:top-1.5', 'peer-data-[size=lg]/menu-button:top-2.5', 'group-data-[collapsible=icon]:hidden', className)} data-sidebar="menu-badge" ref={ref} {...props} />);
SidebarMenuBadge.displayName = 'SidebarMenuBadge';
const SidebarMenuSkeleton = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & {
  showIcon?: boolean;
}>(({
  className,
  showIcon = false,
  ...props
}, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => `${Math.floor(Math.random() * 40) + 50}%`, []);
  return <div className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)} data-sidebar="menu-skeleton" ref={ref} {...props}>
      {showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />}
      <Skeleton className="h-4 max-w-(--skeleton-width) flex-1" data-sidebar="menu-skeleton-text" style={{
      '--skeleton-width': width
    } as React.CSSProperties} />
    </div>;
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';
const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(({
  className,
  ...props
}, ref) => <ul className={cn('mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-sidebar-border border-l px-2.5 py-0.5', 'group-data-[collapsible=icon]:hidden', className)} data-sidebar="menu-sub" ref={ref} {...props} />);
SidebarMenuSub.displayName = 'SidebarMenuSub';
const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({
  ...props
}, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';
const SidebarMenuSubButton = React.forwardRef<HTMLAnchorElement, React.ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}>(({
  asChild = false,
  size = 'md',
  isActive,
  className,
  ...props
}, ref) => {
  const Comp = asChild ? SlotPrimitive.Root : 'a';
  return <Comp className={cn('flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-hidden ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground', 'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground', size === 'sm' && 'text-xs', size === 'md' && 'text-sm', 'group-data-[collapsible=icon]:hidden', className)} data-active={isActive} data-sidebar="menu-sub-button" data-size={size} ref={ref} {...props} />;
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';
export { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupTitle, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, useSidebar };